/* Zivaria product page: variant picking, gallery, add to bag */
(() => {
  if (window.__zvPdp) return;
  window.__zvPdp = true;

  /**
   * Adds items to the cart and lets the theme's cart drawer react.
   * @param {{id: number|string, quantity: number}[]} items
   * @param {HTMLElement} source
   * @param {{openDrawer?: boolean}} [opts] openDrawer false keeps the theme bag drawer closed
   */
  window.zvAddToCart = async (items, source, opts = {}) => {
    const openDrawer = opts.openDrawer !== false;
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean)
      .join(',');

    let Events = null;
    try {
      Events = await import('@shopify/events');
    } catch (e) {
      Events = null;
    }

    let deferred = null;
    if (Events?.CartLinesUpdateEvent) {
      const { CartLinesUpdateEvent } = Events;
      deferred = CartLinesUpdateEvent.createPromise();
      source.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
          promise: deferred.promise,
        })
      );
    }

    const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add';
    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items.map((i) => ({ id: Number(i.id), quantity: i.quantity })), sections: sectionIds }),
    });
    const data = await res.json();

    if (data.status) {
      deferred?.reject?.(new Error(data.message));
      throw new Error(data.description || data.message || 'Could not add to bag');
    }

    const cartUrl = (window.Theme?.routes?.cart_url || '/cart') + '.js';
    const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());

    if (deferred && Events?.CartLinesUpdateEvent) {
      deferred.resolve({
        cart: Events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'zv-product',
          sourceId: 'zv-product',
          itemCount: items.reduce((s, i) => s + i.quantity, 0),
          productId: source.closest('[data-product-id]')?.dataset.productId,
          sections: data.sections,
          didError: false,
        },
      });
      // Open the bag drawer even when the theme's auto-open setting is off
      const drawerHost = document.querySelector('cart-drawer-component');
      const drawer = drawerHost?.closest('theme-drawer');
      if (openDrawer && drawer && !drawerHost.hasAttribute('auto-open') && !drawer.isOpen) drawer.open?.();
      // Theme auto-open is on but we show our own panel: close its drawer again
      if (!openDrawer && drawer && drawerHost.hasAttribute('auto-open')) setTimeout(() => drawer.close?.(), 0);
    } else if (openDrawer) {
      window.location.href = window.Theme?.routes?.cart_url || '/cart';
    }
    return cart;
  };

  /**
   * "Complete the look" side panel. Returns null when the section turns it off.
   * @param {HTMLElement} root
   */
  const setupLook = (root) => {
    const dialog = root.querySelector('[data-zv-look]');
    if (!dialog || typeof dialog.showModal !== 'function') return null;

    const track = dialog.querySelector('[data-zv-look-track]');
    const wrap = dialog.querySelector('[data-zv-look-recs-wrap]');
    const arrows = dialog.querySelector('[data-zv-look-arrows]');
    const prev = dialog.querySelector('[data-zv-look-prev]');
    const next = dialog.querySelector('[data-zv-look-next]');
    const img = dialog.querySelector('[data-zv-look-img]');
    const opts = dialog.querySelector('[data-zv-look-opts]');
    const count = dialog.querySelector('[data-zv-look-count]');

    const syncArrows = () => {
      const overflow = track.scrollWidth > track.clientWidth + 2;
      arrows.hidden = !overflow;
      prev.disabled = track.scrollLeft <= 2;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    const syncEmpty = () => { wrap.hidden = track.children.length === 0; };

    // Shopify's complementary picks (Search & Discovery) replace the fallback when they exist
    let recsLoaded = dialog.dataset.source === 'curated';
    const loadRecs = async () => {
      if (recsLoaded) return;
      recsLoaded = true;
      try {
        const html = await fetch(dialog.dataset.recsUrl).then((r) => (r.ok ? r.text() : ''));
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const cards = [...doc.querySelectorAll('[data-zv-look-recs] > li')].filter(
          (li) => li.dataset.productId !== root.dataset.productId
        );
        if (cards.length >= 3) {
          cards.forEach((li) => {
            const btn = li.querySelector('.zv-look-card__add');
            if (btn) {
              btn.textContent = dialog.dataset.add;
              btn.dataset.label = dialog.dataset.add;
              btn.dataset.added = dialog.dataset.added;
            }
          });
          track.replaceChildren(...cards.map((li) => document.importNode(li, true)));
        }
      } catch (e) {
        /* keep the fallback pieces */
      }
      syncEmpty();
    };

    const close = () => dialog.close();
    dialog.querySelectorAll('[data-zv-look-close]').forEach((b) => b.addEventListener('click', close));
    // Click on the dimmed page closes the panel
    dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); });

    prev.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' }));
    next.addEventListener('click', () => track.scrollBy({ left: track.clientWidth, behavior: 'smooth' }));
    track.addEventListener('scroll', () => requestAnimationFrame(syncArrows), { passive: true });

    // Variant picker inside a suggestion updates its price
    dialog.addEventListener('change', (e) => {
      const select = e.target.closest('.zv-look-card__select');
      if (!select) return;
      const price = select.selectedOptions[0]?.dataset.price;
      const out = select.closest('.zv-look-card').querySelector('[data-zv-look-price]');
      if (price && out) out.textContent = price;
      const btn = select.form.querySelector('.zv-look-card__add');
      btn.textContent = btn.dataset.label;
      btn.classList.remove('is-added');
    });

    // Add a suggestion without leaving the panel
    dialog.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-zv-look-add]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('.zv-look-card__add');
      const id = new FormData(form).get('id');
      if (!id || btn.disabled) return;
      btn.setAttribute('aria-busy', 'true');
      try {
        const cart = await window.zvAddToCart([{ id, quantity: 1 }], form, { openDrawer: false });
        if (cart && count) count.textContent = cart.item_count;
        btn.textContent = btn.dataset.added;
        btn.classList.add('is-added');
      } catch (err) {
        btn.textContent = err.message;
      } finally {
        btn.removeAttribute('aria-busy');
      }
    });

    // Fetch picks early so the panel opens complete
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
    idle(() => loadRecs());
    syncEmpty();

    return {
      open({ image, options, count: n }) {
        if (img && image) img.src = image;
        if (opts) {
          opts.textContent = options || '';
          opts.hidden = !options;
        }
        if (count && typeof n === 'number') count.textContent = n;
        dialog.querySelectorAll('.zv-look-card__add.is-added').forEach((b) => {
          b.textContent = b.dataset.label;
          b.classList.remove('is-added');
        });
        loadRecs();
        dialog.showModal();
        track.scrollLeft = 0;
        requestAnimationFrame(syncArrows);
      },
    };
  };

  const init = (root) => {
    if (root.dataset.zvReady) return;
    root.dataset.zvReady = '1';

    const variants = JSON.parse(root.querySelector('[data-zv-variants]')?.textContent || '[]');
    const form = root.querySelector('[data-zv-form]');
    const idInput = root.querySelector('[data-zv-variant-id]');
    const groups = [...root.querySelectorAll('[data-zv-opt]')];
    const addBtn = root.querySelector('[data-zv-add]');
    const addLabel = root.querySelector('[data-zv-add-label]');
    const addPrice = root.querySelector('[data-zv-add-price]');
    const priceNow = root.querySelector('[data-zv-price-now]');
    const priceBox = root.querySelector('[data-zv-price]');
    const stock = root.querySelector('[data-zv-stock]');
    const error = root.querySelector('[data-zv-error]');
    const media = root.querySelector('[data-zv-media]');
    const counter = root.querySelector('[data-zv-counter]');
    const sumName = root.querySelector('[data-zv-sum-name]');
    const sumPrice = root.querySelector('[data-zv-sum-price]');
    const sumOpts = root.querySelector('[data-zv-sum-opts]');

    // Dark summary above the button: BASE name (size) · price · METAL / other choices
    const renderSummary = (variant) => {
      if (!sumName) return;
      let tier = '';
      const rows = [];
      groups.forEach((g) => {
        const checked = g.querySelector('input:checked');
        if (!checked) return;
        const label = checked.dataset.label || checked.value;
        if (g.hasAttribute('data-zv-carat')) tier = label;
        else rows.push([g.dataset.name, label]);
      });
      // Metal shown without a choice (piece has no Finish option)
      const fixed = root.querySelector('[data-zv-static-metal]');
      if (fixed && fixed.dataset.label) rows.unshift([fixed.dataset.name, fixed.dataset.label]);
      sumName.textContent = sumName.dataset.base + (tier ? ` (${tier})` : '');
      if (variant) sumPrice.textContent = variant.price;
      sumOpts.replaceChildren(
        ...rows.map(([name, value]) => {
          const li = document.createElement('li');
          const strong = document.createElement('strong');
          strong.textContent = name;
          li.append(strong, ' ', value);
          return li;
        })
      );
    };

    const selection = () => {
      const sel = {};
      groups.forEach((g) => {
        const checked = g.querySelector('input:checked');
        sel[Number(g.dataset.zvOpt)] = checked ? checked.value : null;
      });
      return sel;
    };

    const findVariant = (sel) =>
      variants.find((v) => Object.entries(sel).every(([pos, val]) => val === null || v.options[pos - 1] === val));

    const markAvailability = (sel) => {
      groups.forEach((g) => {
        const pos = Number(g.dataset.zvOpt);
        g.querySelectorAll('input:not([disabled])').forEach((input) => {
          const trial = { ...sel, [pos]: input.value };
          const matches = variants.filter((v) =>
            Object.entries(trial).every(([p, val]) => val === null || v.options[p - 1] === val)
          );
          const label = input.closest('label');
          label.classList.toggle('is-impossible', matches.length === 0);
          label.classList.toggle('is-unavailable', matches.length > 0 && !matches.some((m) => m.available));
        });
      });
    };

    const showMedia = (mediaId) => {
      if (!media || !mediaId) return;
      const item = media.querySelector(`[data-media-id="${mediaId}"]`);
      if (!item) return;
      media.querySelectorAll('.is-lead').forEach((el) => el.classList.remove('is-lead'));
      item.classList.add('is-lead');
      media.prepend(item);
      if (media.scrollWidth > media.clientWidth) media.scrollTo({ left: 0, behavior: 'smooth' });
    };

    const update = (fromUser) => {
      const sel = selection();
      let variant = findVariant(sel);

      // If the exact combination doesn't exist, keep the option just clicked and fix the others
      if (!variant && fromUser) {
        const pos = Number(fromUser.closest('[data-zv-opt]').dataset.zvOpt);
        variant =
          variants.find((v) => v.options[pos - 1] === fromUser.value && v.available) ||
          variants.find((v) => v.options[pos - 1] === fromUser.value);
        if (variant) {
          groups.forEach((g) => {
            const p = Number(g.dataset.zvOpt);
            const input = [...g.querySelectorAll('input')].find((i) => i.value === variant.options[p - 1]);
            if (input) input.checked = true;
          });
        }
      }

      markAvailability(selection());
      renderSummary(variant);
      if (error) error.hidden = true;

      if (!variant) {
        addBtn.disabled = true;
        addLabel.textContent = addLabel.dataset.soldout;
        return;
      }

      idInput.value = variant.id;
      priceNow.textContent = variant.price;
      addPrice.textContent = variant.price;
      priceBox.querySelector('[data-zv-compare]')?.remove();
      if (variant.compare) {
        const s = document.createElement('s');
        s.dataset.zvCompare = '';
        s.textContent = variant.compare;
        priceBox.prepend(s);
      }
      addBtn.disabled = !variant.available;
      addLabel.textContent = variant.available ? addLabel.dataset.add : addLabel.dataset.soldout;
      if (stock) {
        stock.textContent = variant.available ? stock.dataset.in : stock.dataset.out;
        stock.classList.toggle('is-out', !variant.available);
      }
      if (fromUser) {
        showMedia(variant.media);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(history.state, '', url);
      }
    };

    groups.forEach((g) => g.addEventListener('change', (e) => update(e.target)));
    update(null);

    const look = setupLook(root);

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (addBtn.disabled) return;
      addBtn.setAttribute('aria-busy', 'true');
      try {
        const cart = await window.zvAddToCart([{ id: idInput.value, quantity: 1 }], form, { openDrawer: !look });
        if (look) {
          const variant = variants.find((v) => String(v.id) === String(idInput.value));
          const picked = [];
          groups.forEach((g) => {
            const checked = g.querySelector('input:checked');
            if (checked) picked.push(checked.dataset.label || checked.value);
          });
          look.open({ image: variant?.image, options: picked.join(' · '), count: cart?.item_count });
        }
        addLabel.textContent = addLabel.dataset.added;
        setTimeout(() => { addLabel.textContent = addLabel.dataset.add; }, 2200);
      } catch (err) {
        if (error) {
          error.textContent = err.message;
          error.hidden = false;
        }
      } finally {
        addBtn.removeAttribute('aria-busy');
      }
    });

    // Keep page content clear of the fixed bag bar on phones
    const buyBar = root.querySelector('[data-zv-buy]');
    if (buyBar && 'ResizeObserver' in window) {
      new ResizeObserver(() => {
        const fixed = getComputedStyle(buyBar).position === 'fixed';
        document.documentElement.style.setProperty('--zv-buybar-h', fixed ? `${buyBar.offsetHeight}px` : '0px');
      }).observe(buyBar);
    }

    // Mobile gallery counter
    if (media && counter) {
      let raf = 0;
      media.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(media.scrollLeft / media.clientWidth) + 1;
          counter.textContent = String(Math.min(i, media.children.length));
        });
      }, { passive: true });
    }
  };

  const start = () => document.querySelectorAll('[data-zv-pdp]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
